import type { Toast as ToastType } from '@/stores/toastStore'
import React from 'react'
import styled, { css, keyframes } from 'styled-components'
import ErrorIcon from '../../icons/ErrorIcon'
import ToastSuccessIcon from '../../icons/ToastSuccessIcon'

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`

const ToastWrapper = styled.div<{ $isVisible: boolean; $type: ToastType['type'] }>`
  position: fixed;
  bottom: 5.5rem;
  left: 50%;
  transform: translateX(-50%);
  width: calc(24.5625rem - 3rem);
  max-width: 600px;
  padding: 1rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  z-index: 9999;
  background-color: ${({ theme }) => theme.palette.gray[600]};
  color: #ffffff;

  animation: ${({ $isVisible }) =>
    $isVisible
      ? css`
          ${fadeIn} 0.5s ease-in-out
        `
      : css`
          ${fadeOut} 0.5s ease-in-out
        `};

  @media screen and (max-width: 37.5rem) {
    width: calc(100% - 3rem);
  }
`

const IconWrapper = styled.div`
  flex-shrink: 0;
  width: 1.25rem;
  height: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
`

const Message = styled.p`
  flex: 1;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.3;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`

interface ToastProps {
  toast: ToastType
  onClose: () => void
}

const Toast: React.FC<ToastProps> = ({ toast, onClose }) => {
  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return <ToastSuccessIcon />
      case 'error':
        return <ErrorIcon color="#ffffff" />
      case 'info':
      default:
        return null
    }
  }

  return (
    <ToastWrapper
      $isVisible={toast.isVisible}
      $type={toast.type}
      onClick={onClose}
      role="alert"
      aria-live="polite"
    >
      {getIcon() && <IconWrapper>{getIcon()}</IconWrapper>}
      <Message>{toast.message}</Message>
    </ToastWrapper>
  )
}

export default Toast
